import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Trash2, X, Key } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const blank = { email: "", password: "", name: "", phone: "", designation_id: "", employee_code: "" };

export default function AdminEmployees() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [editing, setEditing] = useState(null);
    const [f, setF] = useState(blank);

    const load = async () => {
        const [e, d] = await Promise.all([api.get("/admin/employees"), api.get("/admin/designations")]);
        setItems(e.data); setDesignations(d.data);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!f.name || !f.email || !f.designation_id) return toast.error("Fill required");
        try {
            if (editing === "new") {
                if (!f.password) return toast.error("Password required for new employees");
                await api.post("/admin/employees", f);
            } else {
                await api.put(`/admin/employees/${editing}`, { name: f.name, phone: f.phone, designation_id: f.designation_id, employee_code: f.employee_code });
            }
            toast.success("Saved"); setEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/employees/${id}`); load(); };
    const resetPw = async (id) => {
        const pw = window.prompt("New password (min 6):"); if (!pw) return;
        try { await api.put(`/admin/employees/${id}/password`, null, { params: { new_password: pw } }); toast.success("Password reset"); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    return (
        <div data-testid="admin-employees">
            <Toaster position="top-center" />
            <div className="flex justify-between items-center mb-8">
                <div><div className="label-eyebrow text-sky-700">Staff</div><h1 className="font-serif text-4xl text-stone-900 mt-2">Employees</h1></div>
                <Button onClick={() => { setEditing("new"); setF(blank); }} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="add-employee"><Plus className="w-4 h-4 mr-2" />Add employee</Button>
            </div>

            {editing && (
                <div className="bg-white border border-stone-200 p-5 mb-6 space-y-3">
                    <div className="flex justify-between"><h2 className="font-serif text-2xl">{editing === "new" ? "New" : "Edit"} employee</h2><Button variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button></div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <CF label="Full name *" v={f.name} on={(v) => setF({ ...f, name: v })} t="emp-name" />
                        <CF label="Email *" v={f.email} on={(v) => setF({ ...f, email: v })} type="email" disabled={editing !== "new"} t="emp-email" />
                        <CF label="Phone" v={f.phone} on={(v) => setF({ ...f, phone: v })} />
                        <CF label="Employee code" v={f.employee_code} on={(v) => setF({ ...f, employee_code: v })} />
                        {editing === "new" && <CF label="Password *" v={f.password} on={(v) => setF({ ...f, password: v })} type="password" t="emp-pwd" />}
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Designation *</Label>
                            <Select value={f.designation_id} onValueChange={(v) => setF({ ...f, designation_id: v })}>
                                <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="emp-designation"><SelectValue placeholder="Choose…" /></SelectTrigger>
                                <SelectContent>{designations.map(d => <SelectItem key={d.id} value={d.id}>{d.name} · [{(d.capabilities || []).join(", ")}]</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="emp-save">Save</Button>
                </div>
            )}

            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="bg-stone-50 border-b border-stone-200">
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Name</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Email</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Designation</th>
                        <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Capabilities</th>
                        <th></th>
                    </tr></thead>
                    <tbody>
                        {items.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-stone-500">No employees yet.</td></tr> :
                            items.map(e => (
                                <tr key={e.id} className="border-b border-stone-100" data-testid={`emp-row-${e.id}`}>
                                    <td className="p-3 font-medium">{e.name}<div className="text-[10px] text-stone-400">{e.employee_code}</div></td>
                                    <td className="p-3 text-stone-600">{e.email}</td>
                                    <td className="p-3">{e.designation_name}</td>
                                    <td className="p-3 text-xs">{(e.capabilities || []).join(", ")}</td>
                                    <td className="p-3 text-right space-x-2">
                                        <button onClick={() => { setEditing(e.id); setF({ ...blank, ...e }); }} className="text-stone-700 underline text-xs">Edit</button>
                                        {isAdmin && <button onClick={() => resetPw(e.id)} className="text-stone-700 inline-flex items-center text-xs"><Key className="w-3.5 h-3.5 mr-1" />Reset</button>}
                                        <button onClick={() => del(e.id)} className="text-red-700"><Trash2 className="w-4 h-4" /></button>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const CF = ({ label, v, on, type = "text", disabled, t }) => (
    <div><Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label><Input type={type} value={v || ""} onChange={(e) => on(e.target.value)} disabled={disabled} className="rounded-none border-stone-300 mt-1" data-testid={t} /></div>
);
